import { resolveStoredFileUrl } from "utils/storedFileUrl";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { ArrowForward, Category, RestaurantMenu } from "@mui/icons-material";
import {
  Backdrop,
  Card,
  CircularProgress,
  Fade,
  Icon,
  IconButton,
  InputLabel,
  Modal,
  Switch,
  Tooltip,
} from "@mui/material";
import SinDatos from "components/Carteles/SinDatos";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import Table from "components/Tables/Table";
import { MessageManager } from "context";
import { UserContext } from "context/user";
import PropTypes from "prop-types";
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto } from "utils/formatters";
import ProductCard from "./ProductCard";
import { useModalPedido } from "../context/ModalPedidoContext";

const categoriaInicial = {
  categoria: "",
};

const columnsCambioPrecio = [
  { name: "nombre", desc: "Producto", align: "left", width: "50%" },
  { name: "precioActual", desc: "Precio actual", align: "center", width: "25%" },
  { name: "precioNuevo", desc: "Nuevo precio", align: "center", width: "25%" },
];

const getRowsCambioPrecio = (data) =>
  data.map((row) => ({
    nombre: (
      <SoftTypography
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 220,
        }}
        title={row.nombre}
      >
        {row.nombre}
      </SoftTypography>
    ),
    precioActual: (
      <SoftTypography variant="body2" color="text" fontWeight="bold">
        ${formatMoneyPunto(row.precioActual)}
      </SoftTypography>
    ),
    precioNuevo:
      row.precioNuevo === 0 ? (
        <SoftBadge color="primary" badgeContent="SIN PRECIO" container />
      ) : (
        <SoftTypography variant="body2" color="uproGreen" fontWeight="bold">
          ${formatMoneyPunto(row.precioNuevo)}
        </SoftTypography>
      ),
  }));

const CATEGORIAS_POR_PAGINA = 6;
const PRODUCTOS_POR_PAGINA = 8;
const TABS_PRODUCTOS = {
  CATEGORIAS: "categorias",
  FAVORITOS: "favoritos",
};

const createOptimisticDetalleKey = (idProducto) =>
  `optimistic:${Number(idProducto || 0)}:${Date.now()}:${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const AgregarProducto = ({ stockRefreshToken = 0 }) => {
  const { handleSnackbar } = useContext(MessageManager);
  const { user } = useContext(UserContext);
  const {
    pedido,
    setPedido,
    pedidoPersistido,
    disabled,
    onSincronizarDetalleMesa,
  } =
    useModalPedido();
  const searchTimeoutRef = useRef(null);
  const productosContainerRef = useRef(null);
  const categoriasContainerRef = useRef(null);

  const [tabProductos, setTabProductos] = useState(TABS_PRODUCTOS.CATEGORIAS);
  const [favoritosProductos, setFavoritosProductos] = useState([]);
  const [categoria, setCategoria] = useState(categoriaInicial);
  const [paraLlevar, setParaLlevar] = useState(false);
  const [showPrecioCambioModal, setShowPrecioCambioModal] = useState(false);
  const [nuevoParaLlevar, setNuevoParaLlevar] = useState(null);
  const [preciosCambio, setPreciosCambio] = useState([]);
  const [productosVisibles, setProductosVisibles] = useState(PRODUCTOS_POR_PAGINA);
  const [categoriasVisibles, setCategoriasVisibles] = useState(CATEGORIAS_POR_PAGINA);
  const [busqueda, setBusqueda] = useState("");
  const [debouncedBusqueda, setDebouncedBusqueda] = useState("");
  const [disponibilidadOperativaMap, setDisponibilidadOperativaMap] = useState({});
  const [cantidadesSeleccionadas, setCantidadesSeleccionadas] = useState({});

  const idSucursalStock = Number(pedido?.idSede || user?.sede?.id || user?.idSede || 1);
  const detallesReservadosPorProducto = useMemo(
    () =>
      (pedido?.detalles || []).reduce((acc, detalle) => {
        if (detalle?.estado === "CANCELADO" || detalle?.estado === "ENTREGADO") return acc;

        const idProd = Number(detalle?.producto?.id || detalle?.idProducto);
        if (!idProd) return acc;

        acc[idProd] = (acc[idProd] || 0) + Number(detalle?.cantidad || 0);
        return acc;
      }, {}),
    [pedido?.detalles]
  );

  useEffect(() => {
    setParaLlevar(pedido?.paraLlevar ? 1 : 0);
  }, [pedido?.id, pedido?.paraLlevar]);

  useEffect(
    () => () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    },
    []
  );

  const [getProductos, { data: productos, loading: loadingProductos }] = useLazyQuery(
    gql`
      query productos($filter: JSON, $search: String) {
        productos(filter: $filter, search: $search, order: { field: "nombre", order: ASC }) {
          id
          nombre
          precioCarta
          precioOnline
          precioPorMayor
          precioPorMayorOnline
          esElaborado
          cantidadPorMayor
          tipoProducto {
            id
            tipo
          }
          files {
            file
          }
          seccion
        }
      }
    `,
    {
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-first",
      variables: {
        filter: { esIngrediente: true },
      },
    }
  );

  const { data: stockSucursal } = useQuery(
    gql`
      query getProductos($filter: JSON) {
        materiasPrimas(filter: $filter) {
          id
          nombre
          idProductoReferencia
          stock {
            cantidad
          }
        }
      }
    `,
    {
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-first",
      variables: {
        filter: { tipo: "Plato" },
      },
    }
  );

  const { data: dataCategoria } = useQuery(
    gql`
      query categoriasProducto {
        categoriasProducto(order: { field: "categoria", order: ASC }) {
          id
          categoria
          categoriaPermanente
          file
        }
      }
    `
  );

  const { data: dataFavoritos, loading: loadingFavoritos } = useQuery(
    gql`
      query productosFavoritos($idSede: ID!) {
        productosFavoritos(idSede: $idSede) {
          id
          nombre
          precioCarta
          precioOnline
          precioPorMayor
          precioPorMayorOnline
          esElaborado
          cantidadPorMayor
          tipoProducto {
            id
            tipo
          }
          files {
            file
          }
          seccion
        }
      }
    `,
    {
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-first",
      variables: {
        idSede: idSucursalStock,
      },
      skip: !idSucursalStock,
    }
  );

  const [setProductoFavoritoMutation] = useMutation(
    gql`
      mutation setProductoFavorito($idProducto: ID!, $idSede: ID!, $favorito: Boolean!) {
        setProductoFavorito(idProducto: $idProducto, idSede: $idSede, favorito: $favorito)
      }
    `
  );

  const [validarDisponibilidadProductos] = useLazyQuery(
    gql`
      query validarDisponibilidadProductos(
        $productos: [DisponibilidadProductoBatchInput!]!
        $idSucursal: ID
      ) {
        validarDisponibilidadProductos(productos: $productos, idSucursal: $idSucursal) {
          idProducto
          cantidad
          ok
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
    }
  );

  const categoriasFiltradas = useMemo(
    () =>
      dataCategoria?.categoriasProducto?.filter(
        (cat) => cat.id !== "16" && cat.id !== "19" && cat?.categoriaPermanente === null
      ) || [],
    [dataCategoria]
  );

  const stockElaboradoPorProducto = useMemo(
    () =>
      (stockSucursal?.materiasPrimas || []).reduce((acc, materiaPrima) => {
        const idProducto = Number(materiaPrima?.idProductoReferencia);
        if (!idProducto) return acc;

        acc[idProducto] = materiaPrima?.stock?.cantidad ?? 0;
        return acc;
      }, {}),
    [stockSucursal]
  );

  useEffect(() => {
    setFavoritosProductos(dataFavoritos?.productosFavoritos || []);
  }, [dataFavoritos]);

  useEffect(() => {
    if (tabProductos !== TABS_PRODUCTOS.CATEGORIAS) {
      return;
    }

    const filter = {
      disponibilidad: 1,
      idCategoria: debouncedBusqueda
        ? categoriasFiltradas.map((cat) => Number(cat?.id))
        : categoria?.id,
    };

    getProductos({
      variables: {
        filter,
        ...(debouncedBusqueda ? { search: debouncedBusqueda } : {}),
      },
    });
  }, [categoria?.id, categoriasFiltradas, debouncedBusqueda, getProductos, tabProductos]);

  useEffect(() => {
    const productosDisponibles = (() => {
      if (tabProductos === TABS_PRODUCTOS.FAVORITOS) {
        return favoritosProductos;
      }

      const debeMostrarProductos = (!!categoria?.id && !debouncedBusqueda) || !!debouncedBusqueda;
      return debeMostrarProductos ? productos?.productos || [] : [];
    })();
    const productosVisiblesParaValidacion = productosDisponibles.slice(0, productosVisibles);

    if (productosVisiblesParaValidacion.length === 0) {
      setDisponibilidadOperativaMap({});
      return;
    }

    const productosBatch = productosVisiblesParaValidacion.map((prod) => {
      const idProd = Number(prod.id);
      const cantidadPendiente = Number(detallesReservadosPorProducto[idProd] || 0);
      const cantidadSeleccionada = Number(cantidadesSeleccionadas[idProd] || 1);

      return {
        idProducto: idProd,
        cantidad: cantidadPendiente + cantidadSeleccionada,
        esElaborado: !!prod.esElaborado,
      };
    });

    let isActive = true;
    validarDisponibilidadProductos({
      variables: {
        productos: productosBatch,
        idSucursal: idSucursalStock,
      },
    })
      .then(({ data }) => {
        if (!isActive) return;

        const map = {};
        (data?.validarDisponibilidadProductos || []).forEach((item) => {
          map[Number(item.idProducto)] = !!item.ok;
        });
        setDisponibilidadOperativaMap(map);
      })
      .catch(() => {
        if (!isActive) return;
        setDisponibilidadOperativaMap({});
      });

    return () => {
      isActive = false;
    };
  }, [
    cantidadesSeleccionadas,
    categoria?.id,
    debouncedBusqueda,
    detallesReservadosPorProducto,
    favoritosProductos,
    idSucursalStock,
    productos,
    productosVisibles,
    stockRefreshToken,
    tabProductos,
    validarDisponibilidadProductos,
  ]);

  const favoritosFiltrados = useMemo(() => {
    const favoritos = favoritosProductos || [];
    const searchTerms = debouncedBusqueda
      .split(" ")
      .map((term) => term.trim().toLowerCase())
      .filter(Boolean);

    if (searchTerms.length === 0) {
      return favoritos;
    }

    return favoritos.filter((producto) =>
      searchTerms.every((term) =>
        String(producto?.nombre || "")
          .toLowerCase()
          .includes(term)
      )
    );
  }, [debouncedBusqueda, favoritosProductos]);

  const productosFuente = useMemo(() => {
    if (tabProductos === TABS_PRODUCTOS.FAVORITOS) {
      return favoritosFiltrados;
    }

    const debeMostrarProductos = (!!categoria?.id && !debouncedBusqueda) || !!debouncedBusqueda;
    return debeMostrarProductos ? productos?.productos || [] : [];
  }, [categoria?.id, debouncedBusqueda, favoritosFiltrados, productos, tabProductos]);

  const productosConStock = useMemo(() => {
    if (productosFuente.length === 0) {
      return [];
    }

    return productosFuente.map((producto) => {
      const cantidadDetalle = Number(detallesReservadosPorProducto[Number(producto.id)] || 0);
      const cantidadSeleccionada = Number(cantidadesSeleccionadas[Number(producto.id)] || 1);
      const stockCantidad = producto?.esElaborado
        ? Number(stockElaboradoPorProducto[Number(producto.id)] ?? 0)
        : null;
      const disponibleOperativo = disponibilidadOperativaMap[Number(producto.id)];
      const stockInsuficiente = disponibleOperativo === false;
      const sinStockAbsoluto = producto?.esElaborado
        ? stockCantidad === 0
        : stockInsuficiente && cantidadSeleccionada <= 1 && cantidadDetalle === 0;

      return {
        ...producto,
        cantidad: cantidadSeleccionada,
        stockCantidad,
        cantidadDetalle,
        disabledStock: sinStockAbsoluto,
        stockInsuficiente,
      };
    });
  }, [
    cantidadesSeleccionadas,
    detallesReservadosPorProducto,
    disponibilidadOperativaMap,
    productosFuente,
    stockElaboradoPorProducto,
  ]);

  useEffect(() => {
    setProductosVisibles(PRODUCTOS_POR_PAGINA);
    if (productosContainerRef.current) {
      productosContainerRef.current.scrollTop = 0;
    }
  }, [categoria?.id, debouncedBusqueda, tabProductos]);

  useEffect(() => {
    setCategoriasVisibles(CATEGORIAS_POR_PAGINA);
    if (categoriasContainerRef.current) {
      categoriasContainerRef.current.scrollTop = 0;
    }
  }, [dataCategoria]);

  const getPrecioProducto = useCallback(
    (item, cantidad) => {
      const cantidadPorMayor = item?.cantidadPorMayor ?? 0;
      const detalleExistente = (pedido?.detalles || []).find(
        (detalle) =>
          Number(detalle?.producto?.id) === Number(item?.id) && detalle?.estado === "PENDIENTE"
      );
      const cantidadDetalle = Number(detalleExistente?.cantidad || 0);

      if (paraLlevar) {
        if (cantidadPorMayor > 0 && cantidad + cantidadDetalle >= cantidadPorMayor) {
          return Number(item?.precioPorMayorOnline ?? 0);
        }

        return Number(item?.precioOnline ?? 0);
      }

      if (cantidadPorMayor > 0 && cantidad + cantidadDetalle >= cantidadPorMayor) {
        return Number(item?.precioPorMayor ?? 0);
      }

      return Number(item?.precioCarta ?? 0);
    },
    [pedido?.detalles, paraLlevar]
  );

  const handleProductUpdate = useCallback((item) => {
    setCantidadesSeleccionadas((prev) => ({
      ...prev,
      [Number(item.id)]: Number(item?.cantidad || 1),
    }));
  }, []);

  const handleAddDetalle = useCallback(
    async (productoSeleccionado, cantidadForzada = null) => {
      if (!pedido?.mozo) {
        handleSnackbar("Debe seleccionar un mozo antes de agregar productos.", "error");
        return;
      }

      const cantidad = Number(cantidadForzada ?? productoSeleccionado?.cantidad ?? 0);
      const productoBase = {
        ...productoSeleccionado,
        cantidad,
      };
      const detallesLocales = pedido?.detalles || [];
      const detallesPersistidos = pedidoPersistido?.detalles || [];
      const detalleLocalExistente = detallesLocales.find(
        (detalle) =>
          Number(detalle?.producto?.id) === Number(productoBase?.id) &&
          detalle?.estado === "PENDIENTE"
      );
      const detallePersistidoExistente = detallesPersistidos.find(
        (detalle) =>
          Number(detalle?.producto?.id) === Number(productoBase?.id) &&
          detalle?.estado === "PENDIENTE"
      );
      const precio = getPrecioProducto(productoBase, cantidad);

      if (!productoBase?.nombre || !cantidad || !precio) {
        handleSnackbar("Debe seleccionar un producto con cantidad y precio validos.", "error");
        return;
      }

      const optimisticKey =
        detalleLocalExistente?.optimisticKey || createOptimisticDetalleKey(productoBase.id);
      const detalleOptimistaNuevo = {
        cantidad,
        estado: "PENDIENTE",
        precio,
        optimisticKey,
        producto: {
          nombre: productoBase.nombre,
          id: productoBase.id,
          cantidadPorMayor: productoBase.cantidadPorMayor,
          combo: productoBase.combo,
          esElaborado: productoBase.esElaborado,
          precioCarta: productoBase.precioCarta,
          precioOnline: productoBase.precioOnline,
          precioPorMayor: productoBase.precioPorMayor,
          precioPorMayorOnline: productoBase.precioPorMayorOnline,
          seccion: productoBase.seccion,
          tipoProducto: productoBase.tipoProducto,
        },
        ingredientesQuitados: [],
      };

      setPedido((prevPedido) => ({
        ...prevPedido,
        detalles: detalleLocalExistente
          ? (prevPedido?.detalles || []).map((detalle) =>
              Number(detalle?.producto?.id) === Number(productoBase?.id) &&
              detalle?.estado === "PENDIENTE"
                ? {
                    ...detalle,
                    precio,
                    cantidad: Number(detalle?.cantidad || 0) + cantidad,
                  }
                : detalle
            )
          : [...(prevPedido?.detalles || []), detalleOptimistaNuevo],
      }));

      setCantidadesSeleccionadas((prev) => ({
        ...prev,
        [Number(productoBase.id)]: 1,
      }));

      await onSincronizarDetalleMesa({
        detalleInput: detallePersistidoExistente
          ? {
              id: detallePersistidoExistente.id,
              idPedido: pedidoPersistido?.id || null,
              idProducto: productoBase.id,
              cantidad: Number(detallePersistidoExistente?.cantidad || 0) + cantidad,
              precio,
              estado: detallePersistidoExistente.estado,
              horarioEntrega: detallePersistidoExistente.horarioEntrega,
              esElaborado: !!productoBase.esElaborado,
              motivoCancelacion: detallePersistidoExistente.motivoCancelacion,
              comentario: detallePersistidoExistente.comentario || "",
              idsIngredientesQuitados: (detallePersistidoExistente.ingredientesQuitados || []).map(
                (ingrediente) => ingrediente.id
              ),
            }
          : {
              idProducto: productoBase.id,
              cantidad,
              precio,
              estado: "PENDIENTE",
              esElaborado: !!productoBase.esElaborado,
              comentario: "",
              idsIngredientesQuitados: [],
            },
        pendingProductId: Number(productoBase.id),
        pendingDetalleId: detallePersistidoExistente?.id
          ? `id:${detallePersistidoExistente.id}`
          : optimisticKey,
      });
    },
    [
      getPrecioProducto,
      handleSnackbar,
      onSincronizarDetalleMesa,
      pedido?.idSede,
      pedido?.detalles,
      pedido?.mozo,
      pedidoPersistido,
    ]
  );

  const calcularPreciosCambio = useCallback(
    (value) =>
      (pedido?.detalles || [])
        .filter((detalle) => detalle.estado !== "ENTREGADO" && detalle.estado !== "CANCELADO")
        .map((detalle) => {
          const cantidadPorMayor = detalle.producto?.cantidadPorMayor ?? 0;
          const cantidad = Number(detalle.cantidad) ?? 0;
          let nuevoPrecio = 0;

          if (value === 1) {
            if (cantidadPorMayor > 0 && cantidad >= cantidadPorMayor) {
              nuevoPrecio = Number(detalle.producto?.precioPorMayorOnline ?? 0);
            } else {
              nuevoPrecio = Number(detalle.producto?.precioOnline ?? 0);
            }
          } else if (cantidadPorMayor > 0 && cantidad >= cantidadPorMayor) {
            nuevoPrecio = Number(
              detalle.producto?.precioPorMayor ?? detalle.producto?.precioCarta ?? 0
            );
          } else {
            nuevoPrecio = Number(detalle.producto?.precioCarta ?? 0);
          }

          return {
            nombre: detalle.producto?.nombre,
            precioActual: detalle.precio,
            precioNuevo: nuevoPrecio,
          };
        }),
    [pedido?.detalles]
  );

  const handleToggleParaLlevar = useCallback(
    (checked) => {
      const value = checked ? 1 : 0;

      if (
        (pedido?.detalles || []).some(
          (detalle) => detalle.estado !== "ENTREGADO" && detalle.estado !== "CANCELADO"
        )
      ) {
        setNuevoParaLlevar(value);
        setPreciosCambio(calcularPreciosCambio(value));
        setShowPrecioCambioModal(true);
        return;
      }

      setParaLlevar(value);
      setPedido((prevPedido) => ({
        ...prevPedido,
        paraLlevar: value,
      }));
    },
    [calcularPreciosCambio, pedido?.detalles, setPedido]
  );

  const handleSearch = useCallback((event) => {
    const value = event.target.value;
    setBusqueda(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedBusqueda(value);
    }, 500);
  }, []);

  const handleCategoriaClick = useCallback((cat) => {
    setCategoria(cat);
  }, []);

  const favoritosIds = useMemo(
    () => new Set((favoritosProductos || []).map((producto) => Number(producto?.id))),
    [favoritosProductos]
  );

  const handleTabProductosChange = useCallback((nextTab) => {
    setTabProductos(nextTab);
  }, []);

  const handleToggleFavorito = useCallback(
    async (producto) => {
      const idProducto = Number(producto?.id);
      if (!idProducto || !idSucursalStock) {
        handleSnackbar("No se pudo identificar el producto o la sede.", "error");
        return;
      }

      const eraFavorito = favoritosIds.has(idProducto);

      setFavoritosProductos((prev) => {
        if (eraFavorito) {
          return prev.filter((item) => Number(item?.id) !== idProducto);
        }

        return [...prev, producto].sort((a, b) =>
          String(a?.nombre || "").localeCompare(String(b?.nombre || ""))
        );
      });

      try {
        await setProductoFavoritoMutation({
          variables: {
            idProducto,
            idSede: idSucursalStock,
            favorito: !eraFavorito,
          },
        });
      } catch (error) {
        setFavoritosProductos((prev) => {
          if (eraFavorito) {
            return [...prev, producto].sort((a, b) =>
              String(a?.nombre || "").localeCompare(String(b?.nombre || ""))
            );
          }

          return prev.filter((item) => Number(item?.id) !== idProducto);
        });
        handleSnackbar("No se pudo actualizar favoritos.", "error");
      }
    },
    [favoritosIds, handleSnackbar, idSucursalStock, setProductoFavoritoMutation]
  );

  const handleVolverCategorias = useCallback(() => {
    setCategoria(categoriaInicial);
    setBusqueda("");
    setDebouncedBusqueda("");
  }, []);

  const handleScrollProductos = useCallback(
    (event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 12) {
        setProductosVisibles((prev) =>
          prev + PRODUCTOS_POR_PAGINA > productosConStock.length
            ? productosConStock.length
            : prev + PRODUCTOS_POR_PAGINA
        );
      }
    },
    [productosConStock.length]
  );

  const handleScrollCategorias = useCallback(
    (event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 12) {
        setCategoriasVisibles((prev) =>
          prev + CATEGORIAS_POR_PAGINA > categoriasFiltradas.length
            ? categoriasFiltradas.length
            : prev + CATEGORIAS_POR_PAGINA
        );
      }
    },
    [categoriasFiltradas.length]
  );

  const mostrarProductosCategorias =
    tabProductos === TABS_PRODUCTOS.CATEGORIAS &&
    ((!!categoria?.id && !debouncedBusqueda) || !!debouncedBusqueda);
  const mostrarListadoProductos =
    tabProductos === TABS_PRODUCTOS.FAVORITOS || mostrarProductosCategorias;
  const loadingListadoProductos =
    tabProductos === TABS_PRODUCTOS.FAVORITOS ? loadingFavoritos : loadingProductos;
  const mensajeSinDatos =
    tabProductos === TABS_PRODUCTOS.FAVORITOS
      ? "No hay productos favoritos."
      : "No hay productos disponibles.";

  return (
    <SoftBox display="flex" flexDirection="column" gap={2}>
      <SoftBox>
        <InputLabel htmlFor="busqueda-producto">
          <SoftTypography component="label" variant="caption" fontWeight="bold">
            Buscar producto
          </SoftTypography>
        </InputLabel>
        <SoftInput
          id="busqueda-producto"
          placeholder="Ej: Coca cola"
          value={busqueda}
          onChange={handleSearch}
          sx={{ width: "100%" }}
          disabled={disabled}
        />
      </SoftBox>

      <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <SoftTypography variant="button" fontWeight="bold">
          ¿Es pedido para llevar?
        </SoftTypography>
        <Switch
          checked={!!paraLlevar}
          onChange={(event) => handleToggleParaLlevar(event.target.checked)}
          disabled={disabled}
        />
      </SoftBox>

      <SoftBox display="flex" flexDirection="column" gap={1}>
        <SoftBox
          sx={{
            alignSelf: "flex-start",
            display: "inline-flex",
            border: "1px solid rgba(15, 23, 42, 0.22)",
            borderRadius: 2.5,
            overflow: "hidden",
            backgroundColor: "#fff",
          }}
        >
          {[
            { key: TABS_PRODUCTOS.CATEGORIAS, label: "Categorias" },
            { key: TABS_PRODUCTOS.FAVORITOS, label: "Favoritos" },
          ].map((item, index) => {
            const isActive = tabProductos === item.key;

            return (
              <SoftBox
                key={item.key}
                component="button"
                type="button"
                onClick={() => handleTabProductosChange(item.key)}
                disabled={disabled}
                sx={{
                  appearance: "none",
                  minWidth: 0,
                  border: "none",
                  px: 2.25,
                  py: 0.75,
                  lineHeight: 1.2,
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  backgroundColor: isActive ? "#344767" : "#fff",
                  color: isActive ? "#fff" : "#344767",
                  borderLeft: index === 0 ? "none" : "1px solid rgba(15, 23, 42, 0.16)",
                  cursor: disabled ? "not-allowed" : "pointer",
                  "&:hover": {
                    backgroundColor: isActive ? "#2b3b58" : "#f8fafc",
                  },
                  "&:disabled": {
                    color: isActive ? "rgba(255,255,255,0.8)" : "rgba(52, 71, 103, 0.55)",
                    backgroundColor: isActive ? "#344767" : "#fff",
                  },
                }}
              >
                {item.label}
              </SoftBox>
            );
          })}
        </SoftBox>

        {mostrarProductosCategorias && !debouncedBusqueda ? (
          <Tooltip title="Volver a categorias" placement="top">
            <span>
              <SoftButton
                size="small"
                circular
                onClick={handleVolverCategorias}
                disabled={disabled}
                sx={{
                  alignSelf: "flex-start",
                  px: 2,
                  py: 0.85,
                  borderRadius: 999,
                  backgroundColor: "#8ea0bf",
                  color: "#fff",
                  boxShadow: "0 8px 18px rgba(90, 110, 145, 0.18)",
                  "&:hover": {
                    backgroundColor: "#7c90b2",
                  },
                  "&.Mui-disabled": {
                    color: "rgba(255, 255, 255, 0.72)",
                  },
                }}
              >
                <Icon sx={{ fontSize: 18, color: "inherit" }}>arrow_back</Icon>
                &nbsp;VOLVER A CATEGORIAS
              </SoftButton>
            </span>
          </Tooltip>
        ) : null}

        {mostrarListadoProductos ? (
          <Card
            sx={{
              borderRadius: 3,
              border: "1px solid rgba(0,0,0,0.12)",
              boxShadow: "none",
              p: 1,
            }}
          >
            <SoftBox
              ref={productosContainerRef}
              sx={{
                maxHeight: 280,
                overflowY: "auto",
                overflowX: "hidden",
              }}
              onScroll={handleScrollProductos}
            >
              <SoftBox display="flex" flexDirection="column" gap={1}>
                {loadingListadoProductos ? (
                  <SoftBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight={180}
                    flexDirection="column"
                    gap={1.5}
                  >
                    <RestaurantMenu sx={{ fontSize: 48, color: getUproColor("uproGreen") }} />
                    <CircularProgress color="success" />
                  </SoftBox>
                ) : productosConStock.length > 0 ? (
                  productosConStock
                    .slice(0, productosVisibles)
                    .map((prod) => (
                      <ProductCard
                        key={prod.id}
                        prod={prod}
                        handleUpdate={handleProductUpdate}
                        handleAddDetalle={handleAddDetalle}
                        paraLlevar={!!paraLlevar}
                        pending={false}
                        isFavorite={favoritosIds.has(Number(prod.id))}
                        onToggleFavorite={handleToggleFavorito}
                      />
                    ))
                ) : (
                  <SinDatos mensaje={mensajeSinDatos} />
                )}
              </SoftBox>
            </SoftBox>
          </Card>
        ) : (
          <Card
            sx={{
              borderRadius: 3,
              border: "1px solid rgba(0,0,0,0.12)",
              boxShadow: "none",
              p: 1,
            }}
          >
            <SoftBox
              ref={categoriasContainerRef}
              sx={{
                maxHeight: 280,
                overflowY: "auto",
                overflowX: "hidden",
              }}
              onScroll={handleScrollCategorias}
            >
              <SoftBox display="flex" flexDirection="column" gap={1}>
                {categoriasFiltradas.slice(0, categoriasVisibles).map((cat) => (
                  <Card
                    key={cat.id}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid rgba(0,0,0,0.12)",
                      boxShadow: "none",
                      mr: 1,
                    }}
                  >
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1.5}
                      p={1.25}
                      onClick={() => handleCategoriaClick(cat)}
                    >
                      <SoftBox display="flex" alignItems="center" gap={1.25} flex={1} minWidth={0}>
                        <SoftBox
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            overflow: "hidden",
                            backgroundColor: "#f5f7fa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {cat?.file ? (
                            <SoftBox
                              component="img"
                              loading="lazy"
                              src={resolveStoredFileUrl(cat.file)}
                              alt={cat.categoria}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Category sx={{ fontSize: 24, color: "#65707f" }} />
                          )}
                        </SoftBox>
                        <SoftTypography
                          variant="h6"
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {cat.categoria}
                        </SoftTypography>
                      </SoftBox>

                      <IconButton onClick={() => handleCategoriaClick(cat)} disabled={disabled}>
                        <ArrowForward fontSize="small" />
                      </IconButton>
                    </SoftBox>
                  </Card>
                ))}
              </SoftBox>
            </SoftBox>
          </Card>
        )}
      </SoftBox>

      <Modal
        aria-labelledby="modal-precio-cambio-title"
        aria-describedby="modal-precio-cambio-description"
        open={showPrecioCambioModal}
        onClose={() => setShowPrecioCambioModal(false)}
        sx={{ width: "100%" }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 300,
        }}
      >
        <Fade in={showPrecioCambioModal}>
          <Card
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "92%", md: "70%" },
              p: 3,
            }}
          >
            <SoftBox display="flex" gap={1} alignItems="center" mb={2}>
              <SoftTypography variant="h6">Cambio de tipo de pedido</SoftTypography>
            </SoftBox>
            <SoftTypography variant="body2" mb={2}>
              Los productos pendientes cambiaran su precio segun la opcion seleccionada. ¿Desea
              continuar?
            </SoftTypography>
            <SoftBox mb={2}>
              <Table columns={columnsCambioPrecio} rows={getRowsCambioPrecio(preciosCambio)} />
            </SoftBox>
            <SoftBox display="flex" justifyContent="flex-end" gap={2}>
              <SoftButton
                variant="gradient"
                color="secondary"
                onClick={() => setShowPrecioCambioModal(false)}
                circular
              >
                Volver
              </SoftButton>
              <SoftButton
                variant="gradient"
                color="uproGreen"
                onClick={() => {
                  setParaLlevar(nuevoParaLlevar);
                  setPedido((prevPedido) => ({
                    ...prevPedido,
                    paraLlevar: nuevoParaLlevar,
                    detalles: (prevPedido?.detalles || []).map((detalle) => {
                      if (detalle.estado === "ENTREGADO" || detalle.estado === "CANCELADO") {
                        return detalle;
                      }

                      const cantidadPorMayor = detalle.producto?.cantidadPorMayor ?? 0;
                      const cantidad = Number(detalle.cantidad) ?? 0;
                      let nuevoPrecio = 0;

                      if (nuevoParaLlevar === 1) {
                        if (cantidadPorMayor > 0 && cantidad >= cantidadPorMayor) {
                          nuevoPrecio = Number(detalle.producto?.precioPorMayorOnline ?? 0);
                        } else {
                          nuevoPrecio = Number(detalle.producto?.precioOnline ?? 0);
                        }
                      } else if (cantidadPorMayor > 0 && cantidad >= cantidadPorMayor) {
                        nuevoPrecio = Number(
                          detalle.producto?.precioPorMayor ?? detalle.producto?.precioCarta ?? 0
                        );
                      } else {
                        nuevoPrecio = Number(detalle.producto?.precioCarta ?? 0);
                      }

                      return {
                        ...detalle,
                        precio: nuevoPrecio,
                      };
                    }),
                  }));
                  setShowPrecioCambioModal(false);
                }}
                circular
              >
                Confirmar
              </SoftButton>
            </SoftBox>
          </Card>
        </Fade>
      </Modal>
    </SoftBox>
  );
};

AgregarProducto.propTypes = {
  stockRefreshToken: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default memo(AgregarProducto);
